// frontend/admin-portal/src/pages/PurchaseOrderDetail.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Badge,
  Alert,
  Spinner,
  Table,
  Input,
  Modal,
  formatMoney,
} from "@fiberhome/shared-ui";
import { purchaseOrderService } from "../services/purchaseOrderService";
import PurchaseOrderStatusBadge from "../components/procurement/PurchaseOrderStatusBadge";
import ReceiveStockModal from "../components/procurement/ReceiveStockModal";
import ReceiptDetailModal from "../components/procurement/ReceiptDetailModal";
import DefaultAccountBadge from "../components/treasury/DefaultAccountBadge";
import SendPOModal from "../components/procurement/SendPOModal";

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sendOpen, setSendOpen] = useState(false);

  // Edit modal state
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Receive modal state
  const [receiveOpen, setReceiveOpen] = useState(false);

  // Receipt detail modal state
  const [receiptModalId, setReceiptModalId] = useState(null);

  // Allocation modal state
  const [allocOpen, setAllocOpen] = useState(false);
  const [allocPreview, setAllocPreview] = useState(null);
  const [allocBusy, setAllocBusy] = useState(false);
  const [allocResult, setAllocResult] = useState(null);
  const [allocError, setAllocError] = useState("");

  // ---- Load ----
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await purchaseOrderService.get(id);
      setPo(data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Not found");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // ---- Edit handlers ----
  const openEdit = () => {
    setEditForm({
      expectedAt: po.expectedAt || "",
      paymentTerms: po.paymentTerms || "",
      shippingAddress: po.shippingAddress || "",
      notes: po.notes || "",
      taxRate: Number(po.taxRate) || 0,
      shipping: Number(po.shipping) || 0,
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await purchaseOrderService.update(po.id, {
        ...editForm,
        taxRate: Number(editForm.taxRate) || 0,
        shipping: Number(editForm.shipping) || 0,
      });
      setEditOpen(false);
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  // ---- Send / cancel / delete ----
  const handleSend = async () => {
    if (!window.confirm("Send this PO to the supplier?")) return;
    try {
      await purchaseOrderService.send(po.id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const handleCancel = async () => {
    const reason = window.prompt("Cancellation reason (optional):");
    if (reason === null) return;
    try {
      await purchaseOrderService.cancel(po.id, reason);
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this draft PO? Cannot be undone.")) return;
    try {
      await purchaseOrderService.remove(po.id);
      navigate("/purchase-orders");
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  // ---- Allocation handlers ----
  const openAllocation = async () => {
    setAllocOpen(true);
    setAllocPreview(null);
    setAllocResult(null);
    setAllocError("");
    setAllocBusy(true);
    try {
      const data = await purchaseOrderService.previewAllocation(po.id);
      setAllocPreview(data);
    } catch (err) {
      setAllocError(
        err.response?.data?.message || err.message || "Preview failed",
      );
    } finally {
      setAllocBusy(false);
    }
  };

  const applyAllocation = async () => {
    setAllocBusy(true);
    setAllocError("");
    try {
      const data = await purchaseOrderService.allocateCosts(po.id, false);
      setAllocResult(data);
      load();
    } catch (err) {
      setAllocError(
        err.response?.data?.message || err.message || "Allocation failed",
      );
    } finally {
      setAllocBusy(false);
    }
  };

  const closeAllocation = () => {
    if (allocBusy) return;
    setAllocOpen(false);
    setAllocPreview(null);
    setAllocResult(null);
    setAllocError("");
  };

  // ---- Render states ----
  if (loading) {
    return (
      <>
        <PageHeader
          title="Purchase Order"
          breadcrumbs={["Home", "Procurement", "…"]}
        />
        <Card>
          <div className="fh-settings-loading">
            <Spinner size="lg" />
          </div>
        </Card>
      </>
    );
  }

  if (error || !po) {
    return (
      <>
        <PageHeader title="Not found" />
        <Alert variant="danger">{error || "Not found"}</Alert>
        <Button
          variant="outline"
          className="mt-3"
          onClick={() => navigate("/purchase-orders")}
        >
          ← Back
        </Button>
      </>
    );
  }

  // ---- Permissions & derived state ----
  const canEdit = ["draft", "sent", "partially_received"].includes(po.status);
  const canSend = po.status === "draft";
  const canCancel = ["draft", "sent", "partially_received"].includes(po.status);
  const canReceive = ["sent", "partially_received"].includes(po.status);
  const canDelete = po.status === "draft";
  const canAllocate =
    ["partially_received", "received"].includes(po.status) &&
    (po.items || []).some((it) => it.productId);

  const itemRows = (po.items || []).map((it) => ({
    ...it,
    remaining: Number(it.quantity) - Number(it.receivedQuantity || 0),
  }));

  const totalOrdered = itemRows.reduce(
    (s, it) => s + Number(it.quantity || 0),
    0,
  );
  const totalReceived = itemRows.reduce(
    (s, it) =>
      s + Math.min(Number(it.receivedQuantity || 0), Number(it.quantity || 0)),
    0,
  );
  const progressPct = totalOrdered
    ? Math.round((totalReceived / totalOrdered) * 100)
    : 0;

  return (
    <>
      <PageHeader
        title={po.code}
        subtitle={`Supplier: ${po.supplier?.name || "—"}`}
        breadcrumbs={["Home", "Procurement", "Purchase Orders", po.code]}
        actions={
          <div className="d-flex gap-2 flex-wrap">
            {canAllocate && (
              <Button variant="outline" onClick={openAllocation}>
                Allocate Landed Cost
              </Button>
            )}
            {canReceive && (
              <Button variant="primary" onClick={() => setReceiveOpen(true)}>
                Receive Stock
              </Button>
            )}
            {canSend && (
              <Button variant="outline" onClick={handleSend}>
                Send
              </Button>
            )}
            {canEdit && (
              <Button variant="outline" onClick={openEdit}>
                Edit
              </Button>
            )}
            {canCancel && (
              <Button variant="ghost" onClick={handleCancel}>
                Cancel PO
              </Button>
            )}
            {canDelete && (
              <Button variant="ghost" onClick={handleDelete}>
                Delete
              </Button>
            )}
            {canSend && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSendOpen(true)}
              >
                Send to Supplier
              </Button>
            )}
          </div>
        }
      />

      {/* ---- Status / Supplier / Totals cards ---- */}
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Card title="Status">
            <div className="fh-kv">
              <div className="fh-kv__row">
                <span className="text-muted">Status</span>
                <PurchaseOrderStatusBadge status={po.status} />
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Created</span>
                <span>{new Date(po.createdAt).toLocaleDateString()}</span>
              </div>
              {po.sentAt && (
                <div className="fh-kv__row">
                  <span className="text-muted">Sent</span>
                  <span>{new Date(po.sentAt).toLocaleDateString()}</span>
                </div>
              )}
              {po.expectedAt && (
                <div className="fh-kv__row">
                  <span className="text-muted">Expected</span>
                  <span>{po.expectedAt}</span>
                </div>
              )}
              <div className="fh-kv__row">
                <span className="text-muted">Receipt Progress</span>
                <span>
                  {progressPct}% ({totalReceived} / {totalOrdered})
                </span>
              </div>
            </div>
          </Card>
        </div>

        <div className="col-md-4">
          <Card title="Supplier">
            <div className="fh-kv">
              <div className="fh-kv__row">
                <span className="text-muted">Name</span>
                <span>{po.supplier?.name || "—"}</span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Contact</span>
                <span>{po.supplier?.contactName || "—"}</span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Email</span>
                <span>{po.supplier?.email || "—"}</span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Phone</span>
                <span>{po.supplier?.phone || "—"}</span>
              </div>
            </div>
          </Card>
        </div>

        <div className="col-md-4">
          <Card title="Totals">
            <div className="fh-kv">
              <div className="fh-kv__row">
                <span className="text-muted">Subtotal</span>
                <span>
                  {po.currency} {Number(po.subtotal).toLocaleString()}
                </span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Tax ({Number(po.taxRate)}%)</span>
                <span>
                  {po.currency} {Number(po.taxAmount).toLocaleString()}
                </span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Shipping</span>
                <span>
                  {po.currency} {Number(po.shipping).toLocaleString()}
                </span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">
                  <strong>Total</strong>
                </span>
                <span style={{ fontWeight: 700, fontSize: 16 }}>
                  {po.currency} {Number(po.totalAmount).toLocaleString()}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div className="mb-3">
        <PricingCalculator
          form={form}
          onChange={(key, value) => set(key, value)}
        />
        <div className="mt-2">
          <DefaultAccountBadge />
        </div>
      </div>

      {/* ---- Origin (requisition / RFQ / quotation) ---- */}
      {(po.requisition || po.rfq || po.quotation) && (
        <Card title="Origin" className="mb-3">
          <div className="row g-3">
            {po.requisition && (
              <div className="col-md-4">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Requisition
                </div>
                <div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      navigate(`/requisitions/${po.requisition.id}`)
                    }
                  >
                    {po.requisition.code} — {po.requisition.title}
                  </Button>
                </div>
              </div>
            )}
            {po.rfq && (
              <div className="col-md-4">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  RFQ
                </div>
                <div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/rfqs/${po.rfq.id}`)}
                  >
                    {po.rfq.code}
                  </Button>
                </div>
              </div>
            )}
            {po.quotation && (
              <div className="col-md-4">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Quotation
                </div>
                <div>
                  <Badge variant="primary">{po.quotation.code}</Badge>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* ---- Items table ---- */}
      <Card title={`Items (${po.items?.length || 0})`} className="mb-3">
        <Table
          columns={[
            {
              key: "product",
              label: "Product",
              render: (r) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{r.productName}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {r.productSku || (
                      <span className="text-muted">(no SKU)</span>
                    )}
                    {r.productId ? (
                      <span style={{ color: "var(--fh-success)" }}>
                        {" "}
                        · linked
                      </span>
                    ) : (
                      <span style={{ color: "var(--fh-warning)" }}>
                        {" "}
                        · not in catalog
                      </span>
                    )}
                  </div>
                </div>
              ),
            },
            { key: "quantity", label: "Ordered" },
            { key: "receivedQuantity", label: "Received" },
            {
              key: "remaining",
              label: "Remaining",
              render: (r) => <strong>{r.remaining}</strong>,
            },
            {
              key: "unitPrice",
              label: "Unit Price",
              render: (r) => formatMoney(r.unitPrice, { symbol: po.currency }),
            },
            {
              key: "lineTotal",
              label: "Total",
              render: (r) => (
                <strong>
                  {formatMoney(r.lineTotal, { symbol: po.currency })}
                </strong>
              ),
            },
          ]}
          data={itemRows}
          emptyMessage="No items"
        />
      </Card>

      {/* ---- Receipts table ---- */}
      <Card title={`Goods Receipts (${po.receipts?.length || 0})`}>
        <Table
          columns={[
            {
              key: "code",
              label: "Receipt #",
              render: (r) => <strong>{r.code}</strong>,
            },
            {
              key: "receivedAt",
              label: "Received",
              render: (r) => new Date(r.receivedAt).toLocaleString(),
            },
            {
              key: "invoiceNumber",
              label: "Invoice #",
              render: (r) =>
                r.invoiceNumber || <span className="text-muted">—</span>,
            },
            {
              key: "status",
              label: "Status",
              render: (r) => (
                <Badge variant={r.status === "posted" ? "success" : "default"}>
                  {r.status}
                </Badge>
              ),
            },
            {
              key: "actions",
              label: "",
              width: 120,
              render: (r) => (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setReceiptModalId(r.id)}
                >
                  View
                </Button>
              ),
            },
          ]}
          data={po.receipts || []}
          emptyMessage="No receipts yet"
        />
      </Card>

      {/* ---- Edit modal ---- */}
      {editOpen && editForm && (
        <div
          className="modal fade show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit {po.code}</h5>
                <button
                  className="btn-close"
                  onClick={() => setEditOpen(false)}
                />
              </div>
              <div className="modal-body">
                <Input
                  label="Expected Delivery"
                  type="date"
                  value={editForm.expectedAt}
                  onChange={(e) =>
                    setEditForm({ ...editForm, expectedAt: e.target.value })
                  }
                />
                <Input
                  label="Tax Rate (%)"
                  type="number"
                  value={editForm.taxRate}
                  onChange={(e) =>
                    setEditForm({ ...editForm, taxRate: e.target.value })
                  }
                />
                <Input
                  label="Shipping"
                  type="number"
                  value={editForm.shipping}
                  onChange={(e) =>
                    setEditForm({ ...editForm, shipping: e.target.value })
                  }
                />
                <Input
                  label="Payment Terms"
                  value={editForm.paymentTerms}
                  onChange={(e) =>
                    setEditForm({ ...editForm, paymentTerms: e.target.value })
                  }
                />
                <Input
                  label="Shipping Address"
                  value={editForm.shippingAddress}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      shippingAddress: e.target.value,
                    })
                  }
                />
                <Input
                  label="Notes"
                  value={editForm.notes}
                  onChange={(e) =>
                    setEditForm({ ...editForm, notes: e.target.value })
                  }
                />
              </div>
              <div className="modal-footer">
                <Button
                  variant="ghost"
                  onClick={() => setEditOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button variant="primary" onClick={saveEdit} loading={saving}>
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- Receive stock modal ---- */}
      <ReceiveStockModal
        open={receiveOpen}
        po={po}
        onClose={() => setReceiveOpen(false)}
        onReceived={load}
      />

      {/* ---- Receipt detail modal ---- */}
      {receiptModalId && (
        <ReceiptDetailModal
          open={!!receiptModalId}
          receiptId={receiptModalId}
          onClose={() => setReceiptModalId(null)}
          onCancelled={load}
        />
      )}

      {/* ---- Allocation modal ---- */}
      <Modal
        open={allocOpen}
        onClose={closeAllocation}
        title={allocResult ? "Landed Cost Applied" : "Allocate Landed Cost"}
        size="xl"
        footer={
          allocResult ? (
            <Button variant="primary" onClick={closeAllocation}>
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={closeAllocation}
                disabled={allocBusy}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={applyAllocation}
                loading={allocBusy}
                disabled={!allocPreview}
              >
                Apply to Products
              </Button>
            </>
          )
        }
      >
        {allocError && (
          <Alert variant="danger" className="mb-3">
            {allocError}
          </Alert>
        )}

        {!allocPreview && allocBusy && (
          <div className="d-flex align-items-center gap-2 p-3">
            <Spinner size="md" />
            <span className="text-muted">Computing allocation…</span>
          </div>
        )}

        {allocPreview && !allocResult && (
          <>
            <Alert variant="info" className="mb-3">
              Freight total{" "}
              <strong>
                {formatMoney(allocPreview.freightTotal, {
                  symbol: po.currency,
                })}
              </strong>{" "}
              will be distributed proportionally across{" "}
              <strong>{allocPreview.lines.length}</strong> lines based on FOB
              value. Each product's <code>costPrice</code> will be updated to
              the landed unit cost.
            </Alert>

            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Products (FOB)
                </div>
                <strong>
                  {formatMoney(allocPreview.productsSubtotal, {
                    symbol: po.currency,
                  })}
                </strong>
              </div>
              <div className="col-md-4">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Freight
                </div>
                <strong>
                  {formatMoney(allocPreview.freightTotal, {
                    symbol: po.currency,
                  })}
                </strong>
              </div>
              <div className="col-md-4">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Grand Total
                </div>
                <strong>
                  {formatMoney(allocPreview.grandTotal, {
                    symbol: po.currency,
                  })}
                </strong>
              </div>
            </div>

            <div className="fh-table-wrap">
              <table className="fh-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>FOB Unit</th>
                    <th>FOB Total</th>
                    <th>Freight Share</th>
                    <th>Landed Total</th>
                    <th>Landed Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {allocPreview.lines.map((line) => (
                    <tr key={line.poItemId}>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                          {line.productName}
                        </div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {line.productSku || <em>no SKU</em>}
                          {!line.productId && (
                            <span style={{ color: "var(--fh-warning)" }}>
                              {" "}
                              · not in catalog
                            </span>
                          )}
                        </div>
                      </td>
                      <td>{line.quantity}</td>
                      <td>
                        {formatMoney(line.fobUnit, { symbol: po.currency })}
                      </td>
                      <td>
                        {formatMoney(line.fobLineTotal, {
                          symbol: po.currency,
                        })}
                      </td>
                      <td>
                        {formatMoney(line.freightShare, {
                          symbol: po.currency,
                        })}
                      </td>
                      <td>
                        {formatMoney(line.landedTotal, { symbol: po.currency })}
                      </td>
                      <td>
                        <strong>
                          {formatMoney(line.landedUnit, {
                            symbol: po.currency,
                            decimals: 4,
                          })}
                        </strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {allocResult && (
          <>
            <Alert variant="success" className="mb-3">
              Updated <strong>{allocResult.results.updated}</strong> product
              {allocResult.results.updated === 1 ? "" : "s"}.
              {allocResult.results.skipped > 0 && (
                <> {allocResult.results.skipped} skipped.</>
              )}
            </Alert>

            <div className="fh-table-wrap">
              <table className="fh-table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Product</th>
                    <th>Old Cost</th>
                    <th>New Cost</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {(allocResult.results.items || []).map((it, idx) => (
                    <tr key={it.poItemId || idx}>
                      <td>
                        <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                          {it.productSku || "—"}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: 13 }}>{it.productName}</div>
                      </td>
                      <td>
                        {it.applied
                          ? formatMoney(it.oldCost, { symbol: po.currency })
                          : "—"}
                      </td>
                      <td>
                        {it.applied ? (
                          <strong>
                            {formatMoney(it.newCost, {
                              symbol: po.currency,
                              decimals: 4,
                            })}
                          </strong>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {it.applied ? (
                          <Badge variant="success">updated</Badge>
                        ) : (
                          <span className="text-muted" style={{ fontSize: 12 }}>
                            {it.reason || "skipped"}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal>
      <SendPOModal
        open={sendOpen}
        po={po}
        supplier={po?.supplier}
        onClose={() => setSendOpen(false)}
      />
    </>
  );
}
